import { cx } from '@flowus/common/cx';
import { useCloseModal } from '@flowus/common/next-modal';
import type { NotificationDTO, SegmentDTO, SpaceDTO } from '@next-space/fe-api-idl';
import {
  BlockType,
  CollectionSchemaType,
  NotificationType,
  TextType,
} from '@next-space/fe-api-idl';
import dayjs from 'dayjs';
import _ from 'lodash-es';
import type { FC } from 'react';
import { useRef } from 'react';
import { NavLink, useHistory } from 'react-router-dom';
import { ICON_MAP } from 'src/bitable/const';
import { PersonValueView } from 'src/bitable/table-view/cell/person';
import { Avatar } from 'src/common/components/avatar';
import { Icon } from 'src/common/components/icon';
import { Tooltip } from 'src/common/components/tooltip';
import { DATE_TIME_FORMAT, TIME_FORMAT, UNNAMED_GROUP } from 'src/common/const';
import { BlockDefaultIcon } from 'src/components/block-default-icon';
import { IconTrigger } from 'src/components/icon-trigger';
import { TimeAgo } from 'src/components/time-ago';
import { DateValue } from 'src/editor/component/date-value';
import { BlockRenderer } from 'src/editor/editor/uikit/block';
import { segmentsToText } from 'src/editor/utils/editor';
import { getOwnerPage } from 'src/hooks/block/use-get-owner-page';
import { useNormalizePath } from 'src/hooks/page/use-normalize-path';
import { useCurrentSpace } from 'src/hooks/space/use-current-space';
import { getUserName } from 'src/hooks/user/use-remark-name';
import { useUser } from 'src/hooks/user/use-user';
import { Images } from 'src/image';
import { Modals } from 'src/modals';
import { cache } from 'src/redux/store';
import { usePageActivity } from 'src/services/page-activities/hook';
import { useObservableBlock, useObservableStore } from 'src/services/rxjs-redux/hook';
import { toLowerCaseFirstLetter } from 'src/utils/string-util';
import { CommentFeed } from '../../drawer/page-feeds/feeds/comment-feed';
import { Breadcrumb } from '../../header/breadcrumb';
import { OpenSettingFrom, SettingMenuType } from '../../setting-modal/type';
import {
  useOpenSettingModal,
  useOpenUpgradeSpace,
} from '../../setting-modal/use-open-setting-modal';
import { SegmentPlainText } from '../toc/helper';
import { ViewPath } from 'src/utils';

const InlinePage: FC<{ uuid?: string; className?: string; toScene?: ViewPath }> = ({
  uuid,
  className,
  toScene,
}) => {
  const block = useObservableBlock(uuid);
  const path = useNormalizePath(uuid ?? '', toScene);

  if (uuid == null) return null;
  return (
    <NavLink
      to={path}
      className={'space-x-0.5'}
      onClick={(e) => {
        if (block?.type === BlockType.FILE) {
          // 如果是文件直接打开
          e.preventDefault();
          window.open(path);
        }
      }}
    >
      <IconTrigger
        iconSize={16}
        className={cx('inline-flex !p-0 transition-none')}
        blockId={uuid}
        offset={[100, 0]}
        defaultIcon={<BlockDefaultIcon uuid={uuid} size="small" className="relative top-0.5" />}
      />
      <span className={cx('text-t2-bold', className)}>
        {!block ? '已删除页面' : uuid && <SegmentPlainText uuid={uuid} />}
      </span>
    </NavLink>
  );
};

export const InvitedNotification: FC<{ data: NotificationDTO }> = ({ data }) => {
  const edit = data.edits?.[0];
  const author = useUser(edit?.authorId);
  const now = dayjs();
  const time = dayjs(data.createdAt);
  const timeLabel = time.isSame(now, 'day')
    ? time.format(TIME_FORMAT)
    : time.isSame(now, 'year')
    ? time.format('M月D日 HH:mm')
    : time.format('YYYY年M月D日 HH:mm');
  const currentSpace = useCurrentSpace();
  const group =
    data.invitedPermissionGroupId == null
      ? null
      : currentSpace.permissionGroups?.find((it) => it.id === data.invitedPermissionGroupId);
  const invitee =
    data.type === 'groupInvited'
      ? `「${group?.name || UNNAMED_GROUP}」`
      : toLowerCaseFirstLetter('你');
  let target = null;
  if (data.navigableBlockId) {
    // 邀请协作
    target = (
      <>
        <InlinePage className="mx-1" uuid={data.navigableBlockId} />
        {!__BUILD_IN__ && '的协作'}
      </>
    );
  } else {
    // 邀请进组
    target = (
      <>
        {__BUILD_IN__ && 'group'}
        <span className="text-t2-bold mx-1">{group?.name || UNNAMED_GROUP}</span>
        {!__BUILD_IN__ && '成员组'}
      </>
    );
  }
  return (
    <div className="flex py-2 px-4 animate-hover-black3 !cursor-default border-grey6">
      <Avatar
        className="mr-2 rounded-full text-white !text-t2-medium"
        imgClassName="rounded-full"
        name={getUserName(author?.uuid ?? '', author?.nickname)}
        color={author?.backgroundColor}
        icon={{ type: 'upload', value: author?.avatar }}
      />
      <div className="flex-1 min-w-0 flex flex-col ">
        <div className="text-t2 flex items-center">
          <div className="pr-2 flex-1 min-w-0 overflow-hidden overflow-ellipsis">
            <span className="text-t2-medium mr-1">
              {getUserName(author?.uuid ?? '', author?.nickname)}
            </span>
            {__BUILD_IN__ ? (
              <>
                invited {invitee} to {target}
              </>
            ) : (
              <>
                邀请 {invitee} 加入 {target}
              </>
            )}
          </div>
          {data.archived ? (
            <div className="rounded-[20px] px-2 text-t4-medium text-grey3 bg-black_006">归档</div>
          ) : (
            !data.read && <div className="rounded-full w-2 h-2 bg-active_color" />
          )}
        </div>
        <span className="text-t4 text-grey3">{timeLabel}</span>
      </div>
    </div>
  );
};

const MentionedProperty: FC<{ className?: string; blockId?: string; property?: string }> = ({
  blockId,
  property,
}) => {
  const propertyObj = useObservableStore(
    ({ blocks }) => {
      if (blockId == null || property == null) return undefined;
      const block = blocks[blockId];
      if (block == null) return undefined;
      const propertyId = /^collectionProperties\.(.+)$/.exec(property)?.[1];
      if (propertyId == null) return undefined;
      const collection = blocks[block.parentId];
      if (collection == null) return undefined;
      return collection.data.schema?.[propertyId];
    },
    [blockId, property]
  );
  if (propertyObj == null) {
    return null;
  }

  return (
    <div className="flex items-center text-t4 text-grey3">
      <Icon name={ICON_MAP[propertyObj.type]} size="middle" className="mr-1" />
      {propertyObj.name}
    </div>
  );
};

const MentionedText: FC<{
  className?: string;
  segments: SegmentDTO[] | undefined;
}> = ({ className, segments = [] }) => {
  return (
    <div className={cx('text-t2', className)}>
      {segments.map((item, index) => {
        if (item.type === TextType.LINK_PAGE && item.uuid) {
          return segmentsToText(cache.blocks[item.uuid]?.data.segments);
        } else if ([TextType.DATETIME, TextType.DATE].includes(item.type)) {
          return (
            <DateValue
              key={index}
              includeAt={true}
              segment={item}
              className="inline-flex"
              normalClassName="mx-1 text-grey"
            />
          );
        } else if (item.type === TextType.USER && item.uuid) {
          return (
            <span key={index} className="text-grey4">
              @{getUserName(item.uuid, cache.users[item.uuid]?.nickname)}
            </span>
          );
        }
        // enter需要改为空格（产品需求）
        return item.text?.replaceAll(/\s+/g, ' ');
      })}
    </div>
  );
};

const MentionedContent: FC<{
  className?: string;
  blockId?: string;
  property?: string;
  onClick?: (bringHash?: boolean) => void;
}> = ({ className, blockId, property, onClick }) => {
  const isCollectionProperty = property?.startsWith('collectionProperties.');
  const isCaptionProperty = property === 'caption';
  const propertyObj = useObservableStore(
    ({ blocks }) => {
      if (blockId == null || property == null) return undefined;
      const block = blocks[blockId];
      if (block == null) return undefined;
      const propertyId = /^collectionProperties\.(.+)$/.exec(property)?.[1];
      if (propertyId == null) return undefined;
      const collection = blocks[block.parentId];
      if (collection == null) return undefined;
      return collection.data.schema?.[propertyId];
    },
    [blockId, property]
  );
  const segments =
    useObservableStore(
      ({ blocks }) => {
        if (blockId == null || property == null) return undefined;
        const block = blocks[blockId];
        if (block == null) return undefined;
        return _.get(block.data, property) as SegmentDTO[] | undefined;
      },
      [blockId, property]
    ) ?? [];
  return (
    <div
      className={cx('relative', className)}
      onClick={() => {
        const bringHash = !(
          propertyObj?.type === CollectionSchemaType.PERSON ||
          (isCaptionProperty && blockId != null)
        );
        onClick?.(bringHash);
      }}
    >
      {isCollectionProperty && <MentionedProperty blockId={blockId} property={property} />}
      {propertyObj?.type === CollectionSchemaType.PERSON ? (
        <div className="flex flex-wrap mx-[-8px]">
          {segments.map((it) => {
            if (it.type !== TextType.USER || it.uuid == null) return null;
            return (
              <PersonValueView
                key={it.uuid}
                className="text-t4 px-2 py-1.5 my-px"
                size="small"
                userId={it.uuid}
              />
            );
          })}
        </div>
      ) : isCaptionProperty && blockId != null ? (
        <div className="mb-[-10px]">
          <BlockRenderer id={blockId} />
        </div>
      ) : (
        <MentionedText segments={segments} />
      )}
      <div className="absolute left-0 top-0 right-0 bottom-0 animate-hover"></div>
    </div>
  );
};

export const MentionedNotification: FC<{ data: NotificationDTO }> = ({ data }) => {
  const edit = data.edits?.[0];
  const author = useUser(edit?.authorId);
  const now = dayjs();
  const time = dayjs(data.createdAt);
  const timeLabel = time.isSame(now, 'day')
    ? time.format(TIME_FORMAT)
    : time.isSame(now, 'year')
    ? time.format('M月D日 HH:mm')
    : time.format('YYYY年M月D日 HH:mm');
  const history = useHistory();
  const pageId = data.navigableBlockId;
  const closeModal = useCloseModal();
  const blockId = edit?.mentionedBlockId ?? edit?.reminderBlockId;
  const property = edit?.mentionedProperty ?? edit?.reminderProperty;
  const isReminderType = data.type === 'reminder';
  return (
    <div className="flex flex-col py-2 px-4 animate-hover-black3 !cursor-default border-grey6">
      <div className="flex">
        {isReminderType ? (
          <div className="mr-2 rounded-full h-6 w-6 flex items-center justify-center bg-red">
            <Icon className=" text-white" name={'IcMenuAlarm'} size="middle" />
          </div>
        ) : (
          <Avatar
            className="mr-2 rounded-full text-white !text-t2-medium"
            imgClassName="rounded-full"
            name={getUserName(author?.uuid ?? '', author?.nickname)}
            color={author?.backgroundColor}
            icon={{ type: 'upload', value: author?.avatar }}
          />
        )}

        <div className="flex-1 min-w-0 flex flex-col ">
          <div className="flex items-center justify-between">
            <div className="text-t2 break-words">
              {!isReminderType && (
                <span className="text-t2-medium mr-1">
                  {getUserName(author?.uuid ?? '', author?.nickname)}
                </span>
              )}
              {__BUILD_IN__ ? 'mentioned you in' : '在'}
              <InlinePage className="mx-1" uuid={data.navigableBlockId} />
              {!__BUILD_IN__ && '提到了你'}
            </div>
            {data.archived ? (
              <div className="rounded-[20px] px-2 text-t4-medium text-grey3 bg-black_006">归档</div>
            ) : (
              !data.read && <div className="rounded-full w-2 h-2 bg-active_color" />
            )}
          </div>
          <div className="flex items-center">
            <span className="text-t4 text-grey3 mr-1">{timeLabel}</span>
            <span className="mx-1 flex-shrink-0 rounded-full bg-grey3 w-[2px] h-[2px]"></span>
            <Breadcrumb pageId={data.navigableBlockId} styleVariant="messages" />
          </div>
        </div>
      </div>
      <MentionedContent
        className={'pl-7 py-0.5'}
        blockId={blockId}
        property={property}
        onClick={(bringHash) => {
          let jumpBlockId = blockId;
          let jumpPageId = pageId;
          if (blockId) {
            const block = cache.blocks[blockId];
            const parentBlock = cache.blocks[block?.parentId ?? ''];
            if (parentBlock?.type === BlockType.TABLE) {
              // 如果是简单表格就定位到简单表格上
              jumpBlockId = parentBlock.uuid;
            } else {
              const ownerPageId = getOwnerPage(blockId);
              // 如果是内嵌思维导图就跳到内嵌上去
              if (ownerPageId && cache.blocks[ownerPageId]?.type === BlockType.MIND_MAPPING) {
                jumpPageId = ownerPageId;
              }
            }
          }
          const hash = bringHash && jumpBlockId ? `#${jumpBlockId}` : '';
          history.push(`/${jumpPageId}${hash}`);
          closeModal(Modals.MESSAGE_NOTIFICATION);
        }}
      />
    </div>
  );
};

const CommentedNotification: FC<{ data: NotificationDTO }> = ({ data }) => {
  const pageActivity = usePageActivity(data.activityId);

  if (pageActivity == null) return null;
  return <CommentFeed pageActivity={pageActivity} alreadyRead={data.read} />;
};

const getSpacePlanExpireDesc = (space: SpaceDTO, data: NotificationDTO) => {
  if (!data.type) return;
  if (data.type === NotificationType.GIVE_SPACE_CAPACITY) {
    return `${space.title} 空间存储容量权益收缩，为了不影响你的使用体验，已超出存储容量，官方单次补偿至2023年12月31日，感谢你的支持。`;
  }

  if (
    [NotificationType.TEAM_EXPIRATION_BEFORE, NotificationType.PERSONAL_EXPIRATION_BEFORE].includes(
      data.type
    )
  ) {
    return `${space.title} 空间即将到期，请及时续费`;
  }

  if ([NotificationType.TEAM_EXPIRATION].includes(data.type)) {
    return `${space.title} 空间付费已到期，付费功能均已失效`;
  }
};

const Time: FC<{ time?: number; className?: string }> = ({ time, className }) => (
  <span className={cx('ml-[38px] text-t4 text-grey4', className)}>
    <Tooltip popup={dayjs(time).format(DATE_TIME_FORMAT)} className="inline">
      <TimeAgo timestamp={time ?? Date.now()} />
    </Tooltip>
  </span>
);

/** 空间已到期通知 */
const SpacePlanExpireNotification: FC<{ data: NotificationDTO }> = ({ data }) => {
  const space = useCurrentSpace();
  const closeModal = useCloseModal();

  const openUpgradeSpace = useOpenUpgradeSpace();

  const desc = getSpacePlanExpireDesc(space, data);

  return (
    <div
      className="py-3 px-4 border-grey6 border-[0.5px] animate-hover-black3 select-text"
      onClick={() => {
        openUpgradeSpace(OpenSettingFrom.notification_inbox);
        closeModal(Modals.MESSAGE_NOTIFICATION);
      }}
    >
      <div className="flex">
        <Avatar
          className="flex-shrink-0 w-[30px] h-[30px]"
          icon={space.icon}
          name={space.title}
          color={space.backgroundColor}
        />
        <div className="overflow-hidden overflow-ellipsis ml-2 text-t2">{desc}</div>
        {!data.read && (
          <div className="ml-1 mr-1 flex-shrink-0 mt-2 rounded-full w-2 h-2 bg-active_color" />
        )}
      </div>
      <Time time={data.updatedAt} />
    </div>
  );
};

const WebChatClipperFailNotification: FC<{ data: NotificationDTO }> = ({ data }) => {
  return (
    <div className="py-3 px-4 border-grey6 border-[0.5px]">
      <div className="flex">
        <img src={Images.wechatClipperFailed} className="w-[30px] h-[30px]" />
        <div className="overflow-hidden overflow-ellipsis ml-2 text-t2">剪藏失败</div>
        {!data.read && (
          <div className="ml-1 mr-1 flex-shrink-0 mt-2 rounded-full w-2 h-2 bg-active_color" />
        )}
      </div>
      <Time time={data.updatedAt} />
    </div>
  );
};

const UniversalInbox: FC<{ data: NotificationDTO }> = ({ data }) => {
  const downTime = useRef(0);
  const space = useCurrentSpace();
  const closeModal = useCloseModal();
  const openSettingModal = useOpenSettingModal();
  const openUpgradeSpace = useOpenUpgradeSpace();

  const edit = data.edits?.[0];

  if (!edit) return null;

  return (
    <div
      className="py-3 px-4 border-grey6 border-[0.5px] animate-hover-black3 select-text"
      onMouseDown={() => {
        downTime.current = Date.now();
      }}
      onMouseUp={() => {
        const millisecond = dayjs().diff(downTime.current, 'millisecond');
        if (millisecond > 1000) {
          return;
        }
        if (window.getSelection().type === 'Range') return;

        // 打开活动中心
        if (edit.action === 'activity') {
          openSettingModal({
            defaultMenuType: SettingMenuType.integral,
            from: OpenSettingFrom.inbox,
          });
        }

        // 打开升级入口
        if (edit.action === 'upgrade') {
          openUpgradeSpace(OpenSettingFrom.inbox);
        }

        if (edit.jumpUrl?.startsWith('https://')) {
          edit.jumpUrl && window.open(edit.jumpUrl);
        }

        closeModal(Modals.MESSAGE_NOTIFICATION);
      }}
    >
      <div className="flex">
        <Avatar
          className="flex-shrink-0 w-[30px] h-[30px]"
          icon={space.icon}
          name={space.title}
          color={space.backgroundColor}
        />
        <div className="overflow-hidden overflow-ellipsis ml-2 text-t2">{edit.content}</div>
        {!data.read && (
          <div className="ml-1 mr-1 flex-shrink-0 mt-2 rounded-full w-2 h-2 bg-active_color" />
        )}
      </div>
      <Time time={data.updatedAt} />
    </div>
  );
};

const SubscribeNotification: FC<{ data: NotificationDTO; str?: string[]; toScene?: ViewPath }> = ({
  data,
  str = [],
  toScene,
}) => {
  const { authorId = '', sharingBlockId = '' } = data.edits?.[0] ?? {};
  const author = useUser(authorId);

  if (!sharingBlockId) return null;

  return (
    <div className="py-3 px-4 border-grey6 border-[0.5px] animate-hover-black3 select-text">
      <div className="flex text-t2 space-x-2">
        <Avatar
          className="rounded-full text-white !text-t2-medium"
          imgClassName="rounded-full"
          name={getUserName(author?.uuid ?? '', author?.nickname)}
          color={author?.backgroundColor}
          icon={{ type: 'upload', value: author?.avatar }}
        />
        <div className="space-x-1">
          <span>{author?.nickname ?? '未知用户'}</span>
          {str[0] && <span>{str[0]}</span>}
          <InlinePage toScene={toScene} uuid={sharingBlockId} />
          {str[1] && <span>{str[1]}</span>}
        </div>
        {!data.read && (
          <div className="flex-shrink-0 mt-1.5 rounded-full w-2 h-2 bg-active_color" />
        )}
      </div>
      <Time time={data.updatedAt} className="ml-8" />
    </div>
  );
};

const ShareTemplateNotification: FC<{ data: NotificationDTO }> = ({ data }) => {
  const { authorId = '' } = data.edits?.[0] ?? {};
  const author = useUser(authorId);
  // @ts-ignore amazing
  let content: string | undefined = data.edits?.[0]?.['template'];
  if (!content) return null;
  // 后端返回的是模板，需要本地替换
  content = content?.replace('{{authorId}}', author?.nickname ?? '未知用户');
  return (
    <div className="py-3 px-4 border-grey6 border-[0.5px] animate-hover-black3 select-text">
      <div className="flex text-t2">
        <Avatar
          className="mr-2 rounded-full text-white !text-t2-medium"
          imgClassName="rounded-full"
          name={getUserName(author?.uuid ?? '', author?.nickname)}
          color={author?.backgroundColor}
          icon={{ type: 'upload', value: author?.avatar }}
        />
        <div>{content}</div>
        {!data.read && (
          <div className="ml-1 mr-1 flex-shrink-0 mt-1.5 rounded-full w-2 h-2 bg-active_color" />
        )}
      </div>
      <Time time={data.updatedAt} className="ml-8" />
    </div>
  );
};

export const NotificationItems: FC<{ data: NotificationDTO }> = ({ data }) => {
  if (data.type === NotificationType.COMMENTED) {
    return <CommentedNotification data={data} />;
  }

  if (data.type === NotificationType.USER_INVITED || data.type === NotificationType.GROUP_INVITED) {
    return <InvitedNotification data={data} />;
  }

  if (data.type === NotificationType.USER_MENTIONED || data.type === NotificationType.REMINDER) {
    return <MentionedNotification data={data} />;
  }

  if (
    data.type === NotificationType.LIMIT_TIME_ACTIVITY ||
    data.type === NotificationType.UNIVERSAL ||
    data.type === NotificationType.COMMON_WITH_MOBILE_PUSH
  ) {
    return <UniversalInbox data={data} />;
  }

  if (data.type === NotificationType.FORM_COMMIT) {
    return <SubscribeNotification str={['在', '提交了表单']} data={data} />;
  }

  if (data.type === NotificationType.PAGE_SUBSCRIBE) {
    return <SubscribeNotification str={['订阅了']} data={data} />;
  }

  if (data.type === NotificationType.PUBLISH_PAGE) {
    return (
      <SubscribeNotification str={['发布了', '更新']} toScene={ViewPath.subscribe} data={data} />
    );
  }

  if (data.type === NotificationType.TEMPLATE) {
    // 分享人收到的通知
    return <ShareTemplateNotification data={data} />;
  }

  // 发现type为normal的才是降级的通知
  const isPersonalExpiration =
    data.type === 'normal' && data.edits?.[0]?.type === 'personalExpiration';
  if (
    data.type &&
    ([
      NotificationType.PERSONAL_EXPIRATION_BEFORE,
      NotificationType.PERSONAL_EXPIRATION,
      NotificationType.TEAM_EXPIRATION,
      NotificationType.TEAM_EXPIRATION_BEFORE,
      NotificationType.GIVE_SPACE_CAPACITY,
      NotificationType.WECHAT_CLIP_ERROR,
    ].includes(data.type) ||
      isPersonalExpiration)
  ) {
    return <SpacePlanExpireNotification data={data} />;
  }

  // @ts-ignore idl type
  if (data.type === 'wechatClipFailed') {
    <WebChatClipperFailNotification data={data} />;
  }

  return null;
};
